import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API from 'shared/api';
import {
  FiRefreshCw,
  FiDatabase,
  FiZap,
  FiClock,
  FiWifi,
  FiServer,
  FiCpu,
  FiAlertTriangle,
  FiTrendingUp,
  FiBarChart2,
  FiShield,
  FiActivity,
} from 'react-icons/fi';

const COLORS = {
  primary: '#2563eb',
  green: '#059669',
  red: '#dc2626',
  amber: '#d97706',
  purple: '#7c3aed',
  teal: '#0d9488',
  cyan: '#0891b2',
  gray: '#6b7280',
  orange: '#ea580c',
};

const UMBRALES = {
  pool_pct: 80,
  cpu_pct: 70,
  latencia_ms: 1000,
};

const fmtUptime = (s) => {
  if (!s && s !== 0) return '--';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
};

const pct = (v) => {
  if (v === null || v === undefined) return 0;
  return Math.round(v * 100);
};

function statusColor(v, critico, warn = critico * 0.6) {
  if (v === null || v === undefined) return COLORS.gray;
  if (v >= critico) return COLORS.red;
  if (v >= warn) return COLORS.amber;
  return COLORS.green;
}

function statusBg(v, critico) {
  const c = statusColor(v, critico);
  if (c === COLORS.red) return '#fef2f2';
  if (c === COLORS.amber) return '#fffbeb';
  if (c === COLORS.green) return '#ecfdf5';
  return '#f3f4f6';
}

// ─── Gauge central de salud ────────────────────────────────────────────────
// Combina CPU + pool + latencia en un solo % de carga (0-100) para saber de un
// vistazo qué tan cerca está el servidor de ponerse lento.
function computeSalud(d, pool, req) {
  const cpuPct = d.cpu_pct ?? null;
  const poolPct = d.pool_usage_pct ?? (pool.max ? (pool.active / pool.max) * 100 : null);
  const lat = req.latencia_ms_prom ?? null;
  const latPct = lat === null ? null : Math.min(100, (lat / UMBRALES.latencia_ms) * 100);

  const valores = [];
  if (cpuPct !== null) valores.push((cpuPct / UMBRALES.cpu_pct) * 100);
  if (poolPct !== null) valores.push((poolPct / UMBRALES.pool_pct) * 100);
  if (latPct !== null) valores.push(latPct);

  if (valores.length === 0) return { pct: null, nivel: 'unknown' };
  const carga = Math.max(...valores);
  const nivel = carga >= 100 ? 'red' : carga >= 60 ? 'amber' : 'green';
  return { pct: Math.round(Math.min(carga, 150)), nivel };
}

function HealthGauge({ pct, nivel }) {
  const color = nivel === 'red' ? COLORS.red : nivel === 'amber' ? COLORS.amber : nivel === 'green' ? COLORS.green : COLORS.gray;
  const label = nivel === 'red' ? 'Riesgo: cerca de colapsar' : nivel === 'amber' ? 'Precaución: acercándose al límite' : nivel === 'green' ? 'Saludable: con margen' : 'Sin datos aún';
  const icon = nivel === 'red' ? <FiAlertTriangle size={18} color={color} /> : nivel === 'amber' ? <FiActivity size={18} color={color} /> : <FiShield size={18} color={color} />;

  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const fill = pct === null ? 0 : Math.min(pct, 100);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '22px', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
        <svg viewBox="0 0 140 140" style={{ width: '140px', height: '140px' }}>
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#eef0f4" strokeWidth="12" />
          <circle
            cx="70" cy="70" r={radius} fill="none" stroke={color} strokeWidth="12"
            strokeDasharray={`${(fill / 100) * circ} ${circ}`}
            strokeDashoffset="0" transform="rotate(-90 70 70)" strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.6s ease, stroke 0.4s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{pct === null ? '--' : pct + '%'}</div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>CARGA</div>
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
          {icon} {label}
        </div>
        <div style={{ fontSize: '13.5px', color: '#475569', marginTop: '8px', maxWidth: '400px', lineHeight: 1.6 }}>
          Mide el recurso más saturado entre CPU ({UMBRALES.cpu_pct}%), conexiones a BD ({UMBRALES.pool_pct}%) y latencia ({UMBRALES.latencia_ms}ms). Verde = holgado, ámbar = precaución, rojo = riesgo.
        </div>
      </div>
    </div>
  );
}

// ─── Gráfico "En vivo" (tiempo real, 1 punto/seg) ───────────────────────────
function LiveChart({ data }) {
  if (!data || data.length < 2) {
    return <div style={{ textAlign: 'center', color: '#64748b', padding: '30px 0' }}>Acumulando datos en vivo...</div>;
  }
  const rows = [
    { key: 'cpu', label: 'CPU %', color: '#38bdf8', unit: '%', umbral: UMBRALES.cpu_pct, max: 100 },
    { key: 'lat', label: 'Latencia', color: '#fb923c', unit: 'ms', umbral: UMBRALES.latencia_ms },
    { key: 'req', label: 'Req/min', color: '#22d3ee', unit: '' },
    { key: 'pool', label: 'Conexiones BD', color: '#a78bfa', unit: '' },
    { key: 'rss', label: 'Memoria MB', color: '#34d399', unit: 'MB' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
      {rows.map((r) => {
        const values = data.map((d) => d[r.key] ?? 0);
        const max = r.max || Math.max(...values, 1) * 1.15;
        const W = 600;
        const H = 140;
        const pad = { top: 12, right: 12, bottom: 20, left: 44 };
        const iw = W - pad.left - pad.right;
        const ih = H - pad.top - pad.bottom;
        const x = (i) => pad.left + (i / (values.length - 1)) * iw;
        const y = (v) => pad.top + ih - (Math.max(0, v) / max) * ih;
        const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
        const lastV = values[values.length - 1];
        return (
          <div key={r.key} style={{ background: '#0e1626', borderRadius: '12px', padding: '12px 14px', border: '1px solid #1e293b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>{r.label}</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: r.color }}>{Math.round(lastV)}{r.unit}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
              {r.umbral !== undefined && (
                <g>
                  <line x1={pad.left} y1={y(r.umbral)} x2={W - pad.right} y2={y(r.umbral)} stroke="#dc2626" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
                </g>
              )}
              <path d={`${path} L${x(values.length - 1).toFixed(1)},${pad.top + ih} L${x(0).toFixed(1)},${pad.top + ih} Z`} fill={r.color} opacity="0.12" />
              <path d={path} fill="none" stroke={r.color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
              <circle cx={x(values.length - 1)} cy={y(lastV)} r="3.5" fill={r.color} />
              <text x={W - pad.right} y={H - 6} textAnchor="end" fill="#475569" fontSize="9">{Math.round(max)}</text>
            </svg>
          </div>
        );
      })}
    </div>
  );
}

// ─── Gráfico de línea interactivo (tooltip) ────────────────────────────────
function LineChart({ series, height = 260, yMax, yLabel, umbral, umbralColor = COLORS.red, color = COLORS.primary }) {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);

  if (!series || series.length < 2) {
    return <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Recolectando datos... (aparece tras unos minutos)</div>;
  }

  const W = 700;
  const H = height;
  const pad = { top: 16, right: 16, bottom: 28, left: 40 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;
  const max = (yMax || Math.max(...series.map((d) => d.value ?? 0), 1)) * 1.1;
  const x = (i) => pad.left + (i / (series.length - 1)) * iw;
  const y = (v) => pad.top + ih - (Math.max(0, v ?? 0) / max) * ih;
  const path = series.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join(' ');

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const ratio = px / rect.width;
    const idx = Math.round(ratio * (series.length - 1));
    if (idx >= 0 && idx < series.length) setHover(idx);
  };

  const gridVals = [0, 0.5, 1].map((f) => Math.round(max * f));

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      style={{ position: 'relative', cursor: 'crosshair' }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height, fontFamily: 'Inter, sans-serif' }}>
        {gridVals.map((gv) => (
          <g key={gv}>
            <line x1={pad.left} y1={y(gv)} x2={W - pad.right} y2={y(gv)} stroke="#eef2f7" strokeWidth="1" />
            <text x={pad.left - 6} y={y(gv) + 4} textAnchor="end" fill="#64748b" fontSize="12" fontWeight="600">{gv}</text>
          </g>
        ))}
        {/* línea de umbral (límite visual) */}
        {umbral !== undefined && umbral !== null && (
          <g>
            <line x1={pad.left} y1={y(umbral)} x2={W - pad.right} y2={y(umbral)} stroke={umbralColor} strokeWidth="1.5" strokeDasharray="6 4" opacity="0.7" />
            <text x={W - pad.right - 4} y={y(umbral) - 6} textAnchor="end" fill={umbralColor} fontSize="12" fontWeight="700">límite {umbral}</text>
          </g>
        )}
        <path d={`${path} L${x(series.length - 1).toFixed(1)},${pad.top + ih} L${x(0).toFixed(1)},${pad.top + ih} Z`} fill={color} opacity="0.08" />
        <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <g>
            <line x1={x(hover)} y1={pad.top} x2={x(hover)} y2={pad.top + ih} stroke="#cbd5e1" strokeWidth="1" />
            <circle cx={x(hover)} cy={y(series[hover].value)} r="4.5" fill={color} />
          </g>
        )}
        <text x={W / 2} y={H - 6} textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600">{yLabel || ''}</text>
      </svg>
      {hover !== null && (
        <div style={{
          position: 'absolute',
          left: `${(x(hover) / W) * 100}%`,
          top: 8,
          transform: 'translateX(-50%)',
          background: '#0f172a',
          color: '#fff',
          padding: '6px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: 700,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {series[hover].value ?? '--'}{yLabel ? ` ${yLabel}` : ''}
        </div>
      )}
    </div>
  );
}

function BarHora({ picos, metric, color, label, umbral }) {
  if (!picos || picos.length === 0) return <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>Sin datos aún</div>;
  const max = Math.max(...picos.map((p) => p[metric] ?? 0), umbral || 0, 1);
  const W = 700;
  const H = Math.max(picos.length * 26 + 20, 120);
  const padL = 120;
  const bw = 460;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, fontFamily: 'Inter, sans-serif' }}>
      {picos.map((p, i) => {
        const y = i * 26 + 16;
        const v = p[metric] ?? 0;
        const w = Math.max((v / max) * bw, 2);
        const hora = p.hora ? new Date(p.hora).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';
        return (
          <g key={i}>
            <text x={padL - 8} y={y + 4} textAnchor="end" fill="#475569" fontSize="12" fontWeight="600">{hora}</text>
            <rect x={padL} y={y - 8} width={w} height="16" rx="4" fill={color} opacity="0.9" />
            <text x={padL + w + 8} y={y + 4} fill="#1e293b" fontSize="12" fontWeight="700">{v}{label === 'lat_max' || label === 'lat_prom' ? 'ms' : ''}</text>
          </g>
        );
      })}
    </svg>
  );
}

function HitRateBar({ label, value, color }) {
  const p = Math.max(0, Math.min(100, pct(value)));
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13.5px', fontWeight: 600, color: '#1e293b', marginBottom: '7px' }}>
        <span>{label}</span>
        <span style={{ color: '#475569', fontWeight: 700 }}>{value !== null && value !== undefined ? (value * 100).toFixed(1) + '%' : '--'}</span>
      </div>
      <div style={{ height: '10px', background: '#eef2f7', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: '5px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function Card({ icon, iconBg, value, label, dot }) {
  return (
    <div className="sa-metrics-card" style={C.card}>
      <div className="sa-metrics-card-icon" style={{ ...C.cardIcon, background: iconBg }}>
        {icon}
      </div>
      <div>
        <div className="sa-metrics-card-value" style={C.cardValue}>{value}</div>
        <div className="sa-metrics-card-label" style={C.cardLabel}>
          {dot && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot, marginRight: 6 }} />}
          {label}
        </div>
      </div>
    </div>
  );
}

const C = {
  wrapper: { padding: '32px 36px', background: '#f4f6f9', minHeight: '100vh', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '26px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' },
  subtitle: { fontSize: '14px', color: '#64748b', marginTop: '4px', fontWeight: 500 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', border: '1px solid #dbe0e7', background: '#fff', color: '#1e293b', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  card: { background: '#fff', borderRadius: '16px', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 6px 16px rgba(0,0,0,0.04)', border: '1px solid #e9edf2', display: 'flex', alignItems: 'center', gap: '15px' },
  cardIcon: { width: '46px', height: '46px', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardValue: { fontSize: '28px', fontWeight: 800, color: '#0f172a', lineHeight: '1.1' },
  cardLabel: { fontSize: '12.5px', color: '#475569', fontWeight: 600, marginTop: '3px' },
  section: { background: '#fff', borderRadius: '18px', padding: '28px 30px', boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 6px 16px rgba(0,0,0,0.04)', border: '1px solid #e9edf2', marginBottom: '24px' },
  sectionTitle: { fontSize: '17px', fontWeight: 800, color: '#0f172a', marginBottom: '18px' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' },
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #f1f5f9', fontSize: '14px' },
  statLabel: { color: '#475569', fontWeight: 600 },
  statValue: { color: '#0f172a', fontWeight: 700 },
  loading: { padding: '80px', textAlign: 'center', color: '#64748b', fontSize: '15px' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '14px 20px', marginBottom: '24px', color: '#dc2626', fontSize: '14px', fontWeight: 600 },
  chipRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  chip: { padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 700, border: '1px solid #dbe0e7', background: '#fff', color: '#475569', cursor: 'pointer' },
  chipActive: { background: COLORS.primary, color: '#fff', borderColor: COLORS.primary },
  lastUpdate: { fontSize: '13px', color: '#64748b', marginTop: '5px', fontWeight: 500 },
};

export default function Metrics() {
  const [data, setData] = useState(null);
  const [hist, setHist] = useState(null);
  const [horas, setHoras] = useState(24);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const liveRef = useRef([]); // buffer en vivo: últimos ~120 puntos (1 por segundo)

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const r = await API.get('/super/metrics');
      if (r.data.status === 'success') {
        setData(r.data.data);
        // acumular punto en vivo (1/seg) para el gráfico de tiempo real
        const dd = r.data.data;
        const p = dd.pool || {};
        const rq = dd.requests || {};
        liveRef.current.push({
          ts: Date.now(),
          cpu: dd.cpu_pct ?? null,
          lat: rq.latencia_ms_prom ?? null,
          req: rq.ultimo_minuto ?? null,
          pool: p.active ?? null,
          rss: dd.rss_mb ?? null,
        });
        if (liveRef.current.length > 120) liveRef.current.shift();
      }
    } catch (e) {
      console.error('Error cargando métricas:', e);
    }
    try {
      const rh = await API.get(`/super/metrics/historico?horas=${horas}`);
      if (rh.data.status === 'success') setHist(rh.data.data);
    } catch (e) {
      console.error('Error cargando histórico:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastUpdate(new Date());
    }
  }, [horas]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 1000);
    return () => clearInterval(t);
  }, [load]);

  const series = useMemo(() => ({
    cpu: (hist?.puntos || []).map((p) => ({ value: p.cpu_pct })),
    ram: (hist?.puntos || []).map((p) => ({ value: p.rss_mb })),
    pool: (hist?.puntos || []).map((p) => ({ value: p.pool_activas })),
    req: (hist?.puntos || []).map((p) => ({ value: p.requests_min })),
    lat: (hist?.puntos || []).map((p) => ({ value: p.latencia_ms })),
    errores: (hist?.puntos || []).map((p) => ({ value: p.errores_500 })),
  }), [hist]);

  if (loading) return <div style={C.loading}>Cargando...</div>;

  const d = data || {};
  const pool = d.pool || {};
  const socks = d.sockets || {};
  const req = d.requests || {};
  const cache = d.cache || {};
  const auth = d.auth_cache || {};

  const salud = computeSalud(d, pool, req);
  const poolPct = d.pool_usage_pct ?? (pool.max ? (pool.active / pool.max) * 100 : null);
  const cpu = d.cpu_pct ?? null;
  const rss = d.rss_mb ?? null;
  const lat = req.latencia_ms_prom ?? null;
  const lastLabel = lastUpdate ? lastUpdate.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--';

  return (
    <div className="sa-metrics-wrapper" style={C.wrapper}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @media (max-width: 768px) {
          .sa-metrics-wrapper { padding: 16px 12px !important; }
          .sa-metrics-cards { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .sa-metrics-card { padding: 14px !important; flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .sa-metrics-card-value { font-size: 20px !important; }
        }
      `}</style>

      <div className="sa-metrics-topbar" style={C.topBar}>
        <div>
          <div className="sa-metrics-title" style={C.title}>Métricas del servidor</div>
          <div style={C.subtitle}>Salud y rendimiento en tiempo real · instancia {d.instance_id || '--'}</div>
          <div style={C.lastUpdate}>Última actualización: {lastLabel} · se refresca cada 5s</div>
        </div>
        <button style={{ ...C.refreshBtn, opacity: refreshing ? 0.5 : 1 }} disabled={refreshing} onClick={() => load(false)}>
          <FiRefreshCw size={14} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {error && <div style={C.errorBox}>{error}</div>}

      {/* ── Gauge de salud central ── */}
      <div className="sa-dash-section" style={{ ...C.section, border: `1px solid ${salud.nivel === 'red' ? '#fecaca' : salud.nivel === 'amber' ? '#fde68a' : '#eef0f4'}` }}>
        <div style={{ ...C.sectionTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiActivity size={15} color={COLORS.gray} /> Salud del servidor
        </div>
        <HealthGauge pct={salud.pct} nivel={salud.nivel} />
      </div>

      {/* ── En vivo (tiempo real, 1s) ── */}
      <div className="sa-dash-section" style={{ ...C.section, background: 'linear-gradient(180deg,#0b1220 0%,#0e1626 100%)', border: '1px solid #1e293b' }}>
        <div style={{ ...C.sectionTitle, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.red, boxShadow: '0 0 8px #dc2626', animation: 'pulse 1.2s ease infinite' }} />
          En vivo · últimos 120 segundos
        </div>
        <LiveChart data={liveRef.current} />
      </div>

      {/* ── Tarjetas con semáforo ── */}
      <div className="sa-metrics-cards" style={C.cards}>
        <Card icon={<FiCpu size={20} color={statusColor(cpu, UMBRALES.cpu_pct)} />} iconBg={statusBg(cpu, UMBRALES.cpu_pct)}
          value={cpu === null ? '--' : cpu + '%'} label="CPU del proceso" dot={statusColor(cpu, UMBRALES.cpu_pct)} />
        <Card icon={<FiServer size={20} color={COLORS.teal} />} iconBg="#f0fdfa" value={rss === null ? '--' : rss + ' MB'} label="Memoria (RSS)" />
        <Card icon={<FiDatabase size={20} color={statusColor(poolPct, UMBRALES.pool_pct)} />} iconBg={statusBg(poolPct, UMBRALES.pool_pct)}
          value={pool.max ? `${pool.active}/${pool.max}` : '--'} label="Conexiones BD" dot={statusColor(poolPct, UMBRALES.pool_pct)} />
        <Card icon={<FiZap size={20} color={pool.exhausted > 0 ? COLORS.red : COLORS.green} />} iconBg={pool.exhausted > 0 ? '#fef2f2' : '#ecfdf5'}
          value={pool.exhausted ?? 0} label="Pool agotado" dot={pool.exhausted > 0 ? COLORS.red : COLORS.green} />
        <Card icon={<FiTrendingUp size={20} color={COLORS.cyan} />} iconBg="#ecfeff" value={req.ultimo_minuto ?? 0} label="Requests / min" />
        <Card icon={<FiAlertTriangle size={20} color={req.errores_500 > 0 ? COLORS.red : COLORS.green} />} iconBg={req.errores_500 > 0 ? '#fef2f2' : '#ecfdf5'}
          value={req.errores_500 ?? 0} label="Errores 500" dot={req.errores_500 > 0 ? COLORS.red : COLORS.green} />
        <Card icon={<FiClock size={20} color={statusColor(lat, UMBRALES.latencia_ms)} />} iconBg={statusBg(lat, UMBRALES.latencia_ms)}
          value={lat === null ? '--' : lat + 'ms'} label="Latencia promedio" dot={statusColor(lat, UMBRALES.latencia_ms)} />
        <Card icon={<FiWifi size={20} color={COLORS.purple} />} iconBg="#f0f4ff" value={socks.activos ?? 0} label="Sockets conectados" />
      </div>

      {/* ── Gráficos de línea (historial) ── */}
      <div className="sa-dash-section" style={C.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={C.sectionTitle}><FiBarChart2 size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Historial del servidor</div>
          <div style={C.chipRow}>
            {[6, 24, 72, 168].map((h) => (
              <button key={h} style={{ ...C.chip, ...(horas === h ? C.chipActive : {}) }} onClick={() => setHoras(h)}>
                {h >= 24 ? `${h / 24}d` : `${h}h`}
              </button>
            ))}
          </div>
        </div>
        <div style={C.grid2}>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>CPU %</div><LineChart series={series.cpu} yMax={100} umbral={UMBRALES.cpu_pct} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Memoria (MB)</div><LineChart series={series.ram} color={COLORS.teal} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Conexiones BD activas</div><LineChart series={series.pool} yMax={pool.max || undefined} umbral={pool.max ? Math.round(pool.max * 0.8) : undefined} color={COLORS.purple} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Requests / min</div><LineChart series={series.req} color={COLORS.cyan} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Latencia (ms)</div><LineChart series={series.lat} umbral={UMBRALES.latencia_ms} color={COLORS.orange} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Errores 500</div><LineChart series={series.errores} color={COLORS.red} /></div>
        </div>
      </div>

      {/* ── Horas más cargadas ── */}
      <div className="sa-dash-section" style={C.section}>
        <div style={C.sectionTitle}>Horas con más carga</div>
        <div style={C.grid2}>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Pico de requests por minuto</div><BarHora picos={hist?.picos} metric="req_pico" color={COLORS.cyan} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>CPU máximo por hora (%)</div><BarHora picos={hist?.picos} metric="cpu_max" color={COLORS.orange} umbral={UMBRALES.cpu_pct} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Latencia máxima por hora</div><BarHora picos={hist?.picos} metric="lat_max" color={COLORS.red} label="lat_max" umbral={UMBRALES.latencia_ms} /></div>
          <div><div style={{ fontSize: '13.5px', color: '#334155', fontWeight: 700, marginBottom: 10 }}>Errores 500 por hora</div><BarHora picos={hist?.picos} metric="errores_total" color={COLORS.purple} /></div>
        </div>
      </div>

      {/* ── Efectividad de caché ── */}
      <div className="sa-dash-section" style={C.section}>
        <div style={C.sectionTitle}>Efectividad de la caché (mayor = menos carga en BD)</div>
        <div style={C.grid2}>
          <div>
            <HitRateBar label="Caché de autenticación" value={auth.hit_rate} color={COLORS.purple} />
            <div style={C.statRow}><span style={C.statLabel}>Hits</span><span style={C.statValue}>{auth.hits ?? 0}</span></div>
            <div style={C.statRow}><span style={C.statLabel}>Misses</span><span style={C.statValue}>{auth.misses ?? 0}</span></div>
            <div style={{ ...C.statRow, borderBottom: 'none' }}><span style={C.statLabel}>Sesiones cacheadas</span><span style={C.statValue}>{auth.entries ?? 0}</span></div>
          </div>
          <div>
            <HitRateBar label="Caché general" value={cache.hit_rate} color={COLORS.teal} />
            <div style={C.statRow}><span style={C.statLabel}>Hits</span><span style={C.statValue}>{cache.hits ?? 0}</span></div>
            <div style={C.statRow}><span style={C.statLabel}>Misses</span><span style={C.statValue}>{cache.misses ?? 0}</span></div>
            <div style={C.statRow}><span style={C.statLabel}>Entradas</span><span style={C.statValue}>{cache.size ?? 0}</span></div>
            <div style={{ ...C.statRow, borderBottom: 'none' }}><span style={C.statLabel}>Desalojos (LRU)</span><span style={C.statValue}>{cache.evictions ?? 0}</span></div>
          </div>
        </div>
      </div>

      <div className="sa-dash-section" style={C.section}>
        <div style={C.sectionTitle}>Pool de conexiones a PostgreSQL</div>
        <div style={C.grid2}>
          <div>
            <div style={C.statRow}><span style={C.statLabel}>Conexiones activas</span><span style={C.statValue}>{pool.active ?? 0}</span></div>
            <div style={C.statRow}><span style={C.statLabel}>Mínimo del pool</span><span style={C.statValue}>{pool.min ?? 0}</span></div>
            <div style={{ ...C.statRow, borderBottom: 'none' }}><span style={C.statLabel}>Máximo del pool</span><span style={C.statValue}>{pool.max ?? 0}</span></div>
          </div>
          <div>
            <div style={C.statRow}><span style={C.statLabel}>Sockets autenticados</span><span style={C.statValue}>{socks.autenticados ?? 0}</span></div>
            <div style={C.statRow}><span style={C.statLabel}>Sockets totales</span><span style={C.statValue}>{socks.activos ?? 0}</span></div>
            <div style={{ ...C.statRow, borderBottom: 'none' }}><span style={C.statLabel}>Uptime del proceso</span><span style={C.statValue}>{fmtUptime(d.uptime_s)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
